import { FEATURE_FLAGS } from "@solomon-apps/config";
import type {
    ClientPositionView,
    SavedDestination
} from "@solomon-apps/shared";
import type { YaasPositionStatus } from "@solomon-apps/shared";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { FormActions } from "@solomon-apps/shared/src/components/ui/form-actions";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger
} from "@solomon-apps/shared/src/components/ui/select";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput
} from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useState } from "react";

import { DisabledReason } from "@/components/DisabledReason";
import { VIEW_ONLY_REASON } from "@/components/ViewOnly";
import { useSavedDestinations } from "@/hooks/useClientSavedDestinations";

import { truncateAddress } from "../../shared/format";
import type { RewardsDestinationChoice } from "../../shared/RewardsDestinationPicker";

import { RewardsRoutingTable } from "./RewardsRoutingTable";

interface RewardsDestinationSectionProps {
    /** Which rewards-destination model this position uses:
     *  - `multiple` — split rewards across several saved destinations
     *    (dropdowns + % splits + Add).
     *  - `single` — one wallet address, no split. */
    variant: "single" | "multiple";
    position: ClientPositionView;
    status: YaasPositionStatus;
    activeAction?: "edit-rewards" | "resubmit";
    viewOnly?: boolean;
    onEdit: () => void;
    /** Phase 1 — when true, the section swaps its display table for the
     *  inline destination editor (saved-destination dropdown + % split). */
    editing: boolean;
    /** Phase 1 confirm — hands the chosen destination up to the orchestrator,
     *  which queues the mutation and runs the verification flow (phase 2). */
    onConfirm: (choice: RewardsDestinationChoice) => void;
    /** Phase 1 discard — exits inline editing with no change. */
    onDiscard: () => void;
    /** Show the section's own "Rewards Destination" heading. Off under the
     *  position page's tabs, where the tab already names it. */
    showTitle?: boolean;
    mixedDestinations?: boolean;
    allowUnchanged?: boolean;
    /** The signed flow is running for a saved choice. */
    signing?: boolean;
    /** The page's source-wallet gate / verification panel, while signing. */
    authorizeSlot?: React.ReactNode;
    /** Why the last signed attempt failed. */
    signError?: string | null;
    /** Whose ownership saving verifies: the position's label and address. */
    owner?: { label: string; address: string };
}

/** The per-variant components take everything the section does except the
 *  `variant` discriminator (the dispatcher has already switched on it). */
type RewardsDestinationVariantProps = Omit<
    RewardsDestinationSectionProps,
    "variant"
>;

interface DestinationRow {
    address: string | null;
    splitPct: number;
}

/** A single editable destination row: the chosen saved-destination `uuid` and
 *  its % split. The editor holds an array of these. */
interface EditRow {
    uuid: string;
    splitPct: number;
}

export function rewardsDestinationDisplayName(args: {
    address: string | null;
    position: ClientPositionView;
    destinations: SavedDestination[];
}): string {
    const { address, position, destinations } = args;
    if (!address) return "—";

    const savedName = destinations
        .find((d) => d.address === address)
        ?.name?.trim();
    if (savedName && savedName !== address) return savedName;

    const accountLabel = position.account.label?.trim();
    if (address === position.account.address) {
        return accountLabel && accountLabel !== address
            ? accountLabel
            : "Position Wallet";
    }

    return "—";
}

/**
 * Rewards Destination section — flat on the page surface.
 *
 * Thin dispatcher: switches on `variant` and delegates to the matching
 * per-variant component. Keeping the two as separate named components (rather
 * than one branch-heavy body) lets `single` diverge from `multiple` freely —
 * see ARCHITECTURE.md principle #2.
 *
 * The "Edit" trigger lives at the section's action row and only shows for
 * Active positions.
 */
export function RewardsDestinationSection({
    variant,
    mixedDestinations = false,
    ...rest
}: RewardsDestinationSectionProps): React.ReactElement {
    if (mixedDestinations && !rest.editing)
        return (
            <Text variant="small">
                Multiple reward destinations. Manage them on Rewards
                Destinations.
            </Text>
        );
    if (variant === "single") {
        return <SingleRewardsDestination {...rest} />;
    }
    return <MultipleRewardsDestination {...rest} />;
}

/**
 * `multiple` variant — split rewards across several saved destinations.
 *
 * Two states share this body:
 *   - Default: a table listing each destination's name / address / % split.
 *   - Editing (phase 1): the inline editor — one saved-destination dropdown +
 *     % split input per row, an "Add destination" button, and split-total
 *     validation. Confirm builds a `RewardsDestinationChoice` from the primary
 *     row and hands it up.
 */
function MultipleRewardsDestination({
    position,
    // status / activeAction / viewOnly / onEdit drove the Edit trigger, which
    // moves to Rewards Destinations; the parent still passes them.
    editing,
    showTitle = true,
    onConfirm,
    onDiscard
}: RewardsDestinationVariantProps): React.ReactElement {
    const { destinations } = useSavedDestinations();

    const destAddress = position.rewardsDestinationAddress;

    // Single destination at 100% for now; modeled as an array so additional
    // destinations + splits can be listed later.
    const rows: DestinationRow[] = [{ address: destAddress, splitPct: 100 }];

    // Inline-editor state lives here (not in the field component) so the
    // Confirm/Discard FormActions can render as a section-level sibling of the
    // Edit button — same row position, same spacing. Modeled as an array of
    // rows so the "Add destination" button can append more; today the backend
    // saves a single destination (the primary row). Default the first row to
    // the position's current destination when it matches a saved one; otherwise
    // the first saved destination.
    const initialUuid =
        destinations.find((d) => d.address === destAddress)?.uuid ??
        destinations[0]?.uuid ??
        "";
    const [editRows, setEditRows] = useState<EditRow[]>([
        { uuid: initialUuid, splitPct: 100 }
    ]);

    // Re-seed the rows each time we enter edit mode so the editor opens on the
    // position's current destination rather than a stale prior pick.
    useEffect(() => {
        if (editing) {
            setEditRows([{ uuid: initialUuid, splitPct: 100 }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);

    const handleSelectUuid = (index: number, uuid: string): void => {
        setEditRows((prev) =>
            prev.map((row, i) => (i === index ? { ...row, uuid } : row))
        );
    };

    const handleSplitChange = (index: number, raw: string): void => {
        const splitPct =
            raw === "" ? 0 : Math.min(100, Math.max(0, Number(raw)));
        if (raw !== "" && Number.isNaN(Number(raw))) return;
        setEditRows((prev) =>
            prev.map((row, i) => (i === index ? { ...row, splitPct } : row))
        );
    };

    const handleAddRow = (): void => {
        setEditRows((prev) => [...prev, { uuid: "", splitPct: 0 }]);
    };

    // Today's save path is single-destination — Confirm sends the primary
    // (first) row. Additional rows are UI-only until the backend accepts a
    // split set.
    const primary =
        destinations.find((d) => d.uuid === editRows[0]?.uuid) ?? null;

    // Splits across all rows must total 100%. When they don't, the % inputs
    // flag error and Confirm is blocked.
    const splitTotal = editRows.reduce((sum, row) => sum + row.splitPct, 0);
    const splitValid = splitTotal === 100;

    const handleConfirm = (): void => {
        if (!primary || !splitValid) return;
        onConfirm({
            address: primary.address,
            label: primary.name,
            isSavedNew: false
        });
    };

    return (
        <section className="flex flex-col gap-4 px-4 md:px-6">
            {showTitle && (
                <header>
                    <Text
                        variant="h2"
                        render={<h2 />}
                        className="text-foreground-base"
                    >
                        Rewards Destination
                    </Text>
                </header>
            )}

            {editing ? (
                <RewardsDestinationInlineEditor
                    destinations={destinations}
                    rows={editRows}
                    onSelectUuid={handleSelectUuid}
                    onSplitChange={handleSplitChange}
                    onAddRow={handleAddRow}
                    splitError={!splitValid}
                    splitTotal={splitTotal}
                />
            ) : (
                <div className="flex flex-col divide-y divide-line-base">
                    {/* Column labels — transactions-table header typography.
                        Each cell carries a transparent 1px LEFT/RIGHT border
                        (`[&>*]:border-x [&>*]:border-transparent`) — same width
                        as the dropdown/input borders, invisible against the bg.
                        Purely a spacing hack: the edit fields' real 1px border
                        shifts their text in by 1px horizontally, so without a
                        matching (invisible) border here the plain text cells
                        would sit 1px off and the columns wouldn't line up across
                        display ↔ edit. It's `border-x`, not `border`, on purpose
                        — a top/bottom border would add 2px and make the cell
                        taller than the `small` text variant. */}
                    <div className="grid grid-cols-[minmax(120px,1.4fr)_minmax(0,3fr)_96px] items-center gap-4 pb-0 md:pb-2 [&>:first-child]:pl-2.5 [&>:last-child]:pr-2 [&>*]:border-x [&>*]:border-transparent">
                        <Text
                            variant="label"
                            render={<span />}
                            className="text-foreground-muted"
                        >
                            Name
                        </Text>
                        <Text
                            variant="label"
                            render={<span />}
                            className="text-foreground-muted"
                        >
                            Address
                        </Text>
                        <Text
                            variant="label"
                            render={<span />}
                            className="text-foreground-muted text-right"
                        >
                            Split
                        </Text>
                    </div>
                    {rows.map((row, index) => {
                        const name = rewardsDestinationDisplayName({
                            address: row.address,
                            position,
                            destinations
                        });
                        return (
                            <div
                                key={row.address ?? `dest-${index}`}
                                className="grid grid-cols-[minmax(120px,1.4fr)_minmax(0,3fr)_96px] items-center gap-4 py-2 md:py-3 [&>:first-child]:pl-2.5 [&>:last-child]:pr-2 [&>*]:border-x [&>*]:border-transparent"
                            >
                                {/* Name */}
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-base truncate"
                                >
                                    {name}
                                </Text>
                                {/* Address */}
                                {row.address ? (
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="text-foreground-muted min-w-0 truncate"
                                    >
                                        {truncateAddress(row.address)}
                                    </Text>
                                ) : (
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="text-foreground-muted"
                                    >
                                        Not set
                                    </Text>
                                )}
                                {/* Split */}
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-base shrink-0 tabular-nums text-right"
                                >
                                    {row.splitPct}%
                                </Text>
                            </div>
                        );
                    })}
                </div>
            )}
            {/* Action row — a section-level sibling of the display/edit body so
                Edit and Confirm/Discard occupy the exact same slot (the
                section's gap-4 is the only spacing; no mt/pt on either). Edit
                swaps the section into the inline editor; the FormActions sit
                right where Edit was. */}
            {editing ? (
                <FormActions
                    className="self-start"
                    primaryLabel="Confirm"
                    onPrimary={handleConfirm}
                    primaryDisabled={!primary || !splitValid}
                    cancelLabel="Discard"
                    onCancel={onDiscard}
                />
            ) : // Read-only here: a wallet's rewards destination is changed
            // from Rewards Destinations, not from a position. The signed
            // edit flow above stays wired for that move.
            null}
        </section>
    );
}

/* ── `single` variant ────────────────────────────────────── */

/**
 * `single` variant: one destination for the whole position.
 *
 * The routing table, then its actions past a full-length rule:
 *
 * 1. Edit Routing puts the routing in edit mode (`editing`): the table
 *    gains its Actions column and the row edits in place
 *    (`RewardsRoutingTable`).
 * 2. The actions become "Verify [Label] ([Address]) ownership to edit its
 *    rewards routing.", Verify Ownership and Cancel. Verify Ownership takes
 *    the kept change to the page's signed flow: the source wallet signs it,
 *    which is the ownership proof, bound to the new destination. Its gate
 *    and panel take the actions' place (`authorizeSlot`) while it runs.
 */
function SingleRewardsDestination({
    allowUnchanged = false,
    position,
    viewOnly = false,
    onEdit,
    editing,
    showTitle = true,
    onConfirm,
    onDiscard,
    signing = false,
    authorizeSlot = null,
    signError = null,
    owner
}: RewardsDestinationVariantProps): React.ReactElement {
    const { destinations } = useSavedDestinations();
    const destAddress = position.rewardsDestinationAddress;
    const displayName = rewardsDestinationDisplayName({
        address: destAddress,
        position,
        destinations
    });
    // The row's editor, and the change it kept.
    const [rowEditing, setRowEditing] = useState(false);
    const [staged, setStaged] = useState<RewardsDestinationChoice | null>(null);
    useEffect(() => {
        setRowEditing(false);
        if (!editing) setStaged(null);
    }, [editing]);

    const saveBlocked = rowEditing
        ? "Keep or Drop the Row's Change First"
        : !staged
          ? "No Change"
          : null;

    let actions: React.ReactElement;
    if (signing) {
        actions = (
            <motion.div key="sign" {...SWAP_MOTION}>
                {authorizeSlot}
            </motion.div>
        );
    } else if (editing) {
        actions = (
            <motion.div
                key="verify"
                {...SWAP_MOTION}
                className="flex flex-col items-start gap-4"
            >
                <Text
                    variant="small"
                    render={<p />}
                    className="text-foreground-base"
                >
                    Verify {owner?.label ?? "this position"}
                    {owner ? ` (${owner.address})` : ""} ownership to edit its
                    rewards routing.
                </Text>
                <div className="flex items-center gap-6">
                    <DisabledReason
                        reason={saveBlocked}
                        label="Verify Ownership"
                    >
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="lg"
                            disabled={saveBlocked !== null}
                            // A failed save lives in the button; its reason on hover.
                            title={signError ?? undefined}
                            onClick={() => staged && onConfirm(staged)}
                        >
                            {signError
                                ? "Not Verified, Retry"
                                : "Verify Ownership"}
                        </Button>
                    </DisabledReason>
                    <Button
                        variant="transparent"
                        interaction="underline"
                        size="lg"
                        className="px-0"
                        onClick={onDiscard}
                    >
                        Cancel
                    </Button>
                </div>
            </motion.div>
        );
    } else {
        actions = (
            <motion.div key="edit" {...SWAP_MOTION}>
                <DisabledReason
                    reason={viewOnly ? VIEW_ONLY_REASON : null}
                    label="Edit Routing"
                >
                    <Button
                        variant="raised"
                        interaction="lifted"
                        size="lg"
                        disabled={viewOnly}
                        onClick={onEdit}
                    >
                        Edit Routing
                    </Button>
                </DisabledReason>
            </motion.div>
        );
    }

    return (
        <section className="flex flex-col gap-4 px-4 md:px-6">
            {showTitle && (
                <header>
                    <Text
                        variant="h2"
                        render={<h2 />}
                        className="text-foreground-base"
                    >
                        Rewards Destination
                    </Text>
                </header>
            )}
            <RewardsRoutingTable
                position={position}
                destinations={destinations}
                currentAddress={destAddress}
                currentLabel={displayName}
                staged={staged}
                editable={editing && !signing}
                rowEditing={rowEditing}
                allowUnchanged={allowUnchanged}
                onEditRow={() => setRowEditing(true)}
                onStage={(choice) => {
                    setStaged(choice);
                    setRowEditing(false);
                }}
                onDiscardRow={() => setRowEditing(false)}
                addRoute={
                    editing &&
                    !signing &&
                    FEATURE_FLAGS.clientMultiDestinationRouting ? (
                        <DisabledReason
                            reason={ADD_ROUTE_REASON}
                            label="+Add Route"
                        >
                            <Button
                                variant="transparent"
                                interaction="underline"
                                size="sm"
                                className="px-0"
                                disabled
                            >
                                +Add Route
                            </Button>
                        </DisabledReason>
                    ) : null
                }
            />
            {/* The routing's actions, a section of their own: a
                full-length rule above, then the button or the ask. */}
            <div className="-mx-4 mt-6 border-t border-line-base px-4 pt-6 md:-mx-6 md:px-6">
                <AnimatePresence initial={false} mode="wait">
                    {actions}
                </AnimatePresence>
            </div>
        </section>
    );
}

/** +Add Route is flagged in ahead of the split it starts: until a second
 *  route can be edited and saved, it says so on demand. */
const ADD_ROUTE_REASON =
    "Splitting rewards across routes is not available yet.";

/** One action swapping for the next in the routing's actions section. */
const SWAP_MOTION = {
    initial: { opacity: 0, y: 4 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -4 },
    transition: { duration: 0.15, ease: [0.16, 1, 0.3, 1] as const }
};

/* ── Phase 1 — inline destination editor ─────────────────── */

interface RewardsDestinationInlineEditorProps {
    destinations: { uuid: string; name: string; address: string }[];
    rows: EditRow[];
    onSelectUuid: (index: number, uuid: string) => void;
    onSplitChange: (index: number, raw: string) => void;
    onAddRow: () => void;
    /** Splits don't total 100% — flag the % inputs and show the message. */
    splitError: boolean;
    /** Current sum of all row splits, for the error message. */
    splitTotal: number;
}

/**
 * Inline phase-1 editor fields: one destination dropdown + % split input per
 * row, with an "Add destination" button that appends another. Purely
 * presentational and fully controlled — the rows state and Confirm/Discard
 * handling live in the parent section so the FormActions can render as a
 * section-level sibling of the Edit button. Each dropdown is keyed on the
 * saved destination's `uuid`, and only offers destinations not already picked
 * in another row (its own selection stays).
 */
function RewardsDestinationInlineEditor({
    destinations,
    rows,
    onSelectUuid,
    onSplitChange,
    onAddRow,
    splitError,
    splitTotal
}: RewardsDestinationInlineEditorProps): React.ReactElement {
    const hasDestinations = destinations.length > 0;
    // Destinations already chosen in any row — used to keep each dropdown's
    // options unique and to disable Add once every destination is in use.
    const usedUuids = new Set(rows.map((r) => r.uuid).filter(Boolean));
    const canAdd = destinations.some((d) => !usedUuids.has(d.uuid));

    return (
        <div className="flex flex-col">
            {/* Column labels — same grid as the display table. */}
            <div className="grid grid-cols-[minmax(120px,1.4fr)_minmax(0,3fr)_96px] items-center gap-4 pb-2 border-b border-line-base [&>:first-child]:pl-2.5 [&>:last-child]:pr-2 [&>*]:border-x [&>*]:border-transparent">
                <Text
                    variant="label"
                    render={<span />}
                    className="text-foreground-muted"
                >
                    Name
                </Text>
                <Text
                    variant="label"
                    render={<span />}
                    className="text-foreground-muted"
                >
                    Address
                </Text>
                <Text
                    variant="label"
                    render={<span />}
                    className="text-foreground-muted text-right"
                >
                    Split
                </Text>
            </div>

            {/* Edit rows + add button — one row per destination, each a long
                dropdown (Name + Address) beside its % split input, mirroring
                the display row. The Add button appends another row. */}
            <div className="my-2 flex flex-col gap-2">
                {rows.map((row, index) => {
                    const selected =
                        destinations.find((d) => d.uuid === row.uuid) ?? null;
                    // Smart options: every destination not chosen in another
                    // row, plus this row's own current pick.
                    const options = destinations.filter(
                        (d) => d.uuid === row.uuid || !usedUuids.has(d.uuid)
                    );
                    return (
                        <div
                            key={index}
                            className="grid grid-cols-[minmax(120px,1.4fr)_minmax(0,3fr)_96px] items-center gap-4"
                        >
                            <div className="col-span-2 min-w-0">
                                <Select
                                    value={row.uuid}
                                    onValueChange={(value) =>
                                        onSelectUuid(index, value ?? "")
                                    }
                                >
                                    <SelectTrigger
                                        surface="sunken"
                                        className="w-full"
                                    >
                                        {/* Render the selected label ourselves
                                            — the value is the uuid, so the
                                            default display would otherwise show
                                            the raw `dest_…` uuid. */}
                                        <Text
                                            variant="small"
                                            render={
                                                <span data-slot="select-value" />
                                            }
                                            className={
                                                selected
                                                    ? undefined
                                                    : "text-foreground-neutral"
                                            }
                                        >
                                            {selected
                                                ? `${selected.name} (${truncateAddress(
                                                      selected.address
                                                  )})`
                                                : hasDestinations
                                                  ? "Select a destination"
                                                  : "No saved rewards destinations"}
                                        </Text>
                                    </SelectTrigger>
                                    <SelectContent
                                        align="start"
                                        alignItemWithTrigger={false}
                                    >
                                        {options.map((d) => (
                                            <SelectItem
                                                key={d.uuid}
                                                value={d.uuid}
                                                className="pl-2.5"
                                            >
                                                <Text
                                                    variant="small"
                                                    render={<span />}
                                                >
                                                    {d.name} (
                                                    {truncateAddress(d.address)}
                                                    )
                                                </Text>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <InputGroup>
                                <InputGroupInput
                                    value={String(row.splitPct)}
                                    inputMode="numeric"
                                    numeric
                                    state={splitError ? "error" : undefined}
                                    aria-label="Split percentage"
                                    onChange={(e) =>
                                        onSplitChange(index, e.target.value)
                                    }
                                />
                                <InputGroupAddon align="inline-end">
                                    <Text
                                        variant="fine-print"
                                        render={<span />}
                                        className="text-foreground-neutral"
                                    >
                                        %
                                    </Text>
                                </InputGroupAddon>
                            </InputGroup>
                        </div>
                    );
                })}
                <div className="flex items-center gap-3">
                    <Button
                        variant="sunken"
                        outline
                        interaction="lifted"
                        size="sm"
                        onClick={onAddRow}
                        disabled={!canAdd}
                        className="self-start shrink-0"
                    >
                        +Add Destination
                    </Button>
                    {splitError && (
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-error-base"
                        >
                            Splits must total 100% (currently {splitTotal}%).
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
}
